import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// #96: gemeinsame Rückfrage für die sechs irreversiblen Aktionen (Rolle entziehen, Nutzer
// deaktivieren, Struktur-Knoten löschen, Vertretung beenden, Sign-off bestätigen/widerrufen,
// Import-Commit) — bisher jeweils ein Klick ohne Rückfrage. Struktur/Escape-Handling 1:1
// übernommen aus defects/retest-guard-dialog.tsx:26-64 (zentriertes <dialog open aria-modal>,
// Escape in der CAPTURE-Phase mit stopPropagation — Begründung dort: ein darunterliegendes
// SidePanel hängt seinen eigenen Escape-Listener in der Bubble-Phase ein und würde ohne
// Gegenmaßnahme zusätzlich zum Dialog schließen). Anders als beim Retest-Guard-Dialog aber als
// EINE gemeinsame Komponente statt sechs Kopien — die sechs Aufrufer unterscheiden sich nur in
// title/message/confirmLabel/danger, nicht in Struktur oder Verhalten.
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  danger = false,
  pending = false,
  confirmDisabled = false,
  children,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  // #88: Sign-off-Dialoge tragen ein Textfeld (Kommentar/Widerrufsbegründung) und müssen den
  // Bestätigen-Knopf sperren, solange eine Pflichtbegründung fehlt. Beides optional, damit die
  // übrigen Aufrufer unverändert bleiben.
  confirmDisabled?: boolean;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    // Siehe retest-guard-dialog.tsx:26-37 für die ausführliche Begründung: CAPTURE-Listener auf
    // `document` feuern vor Bubble-Listenern auf demselben Ziel, unabhängig von der
    // Registrierreihenfolge. stopPropagation() hier verhindert, dass ein darunterliegendes
    // SidePanel (Bubble-Phase-Listener) dasselbe Escape zusätzlich verarbeitet.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onCancel}
        className="absolute inset-0"
      />
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={title}
        className="relative z-10 m-0 w-full max-w-md space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-text-body shadow-xl outline-none"
      >
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        <p className="text-sm text-text-muted">{message}</p>
        {children}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
            className={`rounded-btn px-3 py-1.5 text-sm text-white disabled:opacity-60 ${
              danger ? 'bg-status-fail' : 'bg-brand-accent'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </dialog>
    </div>
  );
}
